import Formatter from '@/Utils/Formatter';
import { convertToPercentage } from '@/Utils/MathUtility';
import { orderItemTypes, orderStages, projectPhases, orderPaymentStatuses, projectVersions } from '@/Models';
import Resources from '@/Resources.js';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';

function getTaxNameByLocation(location) {
    return location?.name;
}

export function buildOrderSummaryModel(cartDetails, pledge, locations) {
    const { projectContext,
        isProjectInFundingPhase,
        isProjectInCrowdfundingEndedPhase,
        isOriginalTypeExpressCrowdfunding } = useProjectContextStore();

    const cart = cartDetails.cart;
    const isEditModeEnabled = cart && pledge?.parentOrder;
    const hasCustomerProjectLocation = cartDetails.cart.customerProjectLocationID !== null;
    let locationTaxName = null;

    const isShippingDisabled = (isProjectInFundingPhase || isProjectInCrowdfundingEndedPhase)
        && !projectContext.settings.earlyShippingCollectionEnabled
        && !isOriginalTypeExpressCrowdfunding;

    if (hasCustomerProjectLocation) {
        const customerCurrentLocation = locations.find(e => e.projectLocationID === cart.customerProjectLocationID);
        const customerCurrentSubLocation = customerCurrentLocation?.subLocations.find(e => e.projectSubLocationID === cart.customerProjectSubLocationID);
        locationTaxName = customerCurrentSubLocation ? getTaxNameByLocation(customerCurrentSubLocation) : getTaxNameByLocation(customerCurrentLocation);
    }

    const currencySymbol = cart.currencySymbol;
    const isHandleTax = cart.handleTax;
    const isVatReverseEnabled = cart.isVatRefundable;
    const isOvercharged = cart.effectivePayableAmount < 0;

    const pledgedOrderStates = [
        { stage: orderStages.pledged, paymentStatus: orderPaymentStatuses.none },
        { stage: orderStages.pledged, paymentStatus: orderPaymentStatuses.paid },
        { stage: orderStages.pledged, paymentStatus: orderPaymentStatuses.scheduled },
    ];

    const orderStatesToShowOutstandingAmount = [
        { stage: orderStages.unfinished, paymentStatus: orderPaymentStatuses.none },
        { stage: orderStages.placed, paymentStatus: orderPaymentStatuses.awaitingPayment },
    ];

    const showCreditDiscount = [
        cart.orderStage !== orderStages.canceled,
        cart.effectiveOutstandingCreditPaymentAmount > 0,
    ].every(element => element);

    // has order outstanding amount conditions
    const hasOrderOutstandingAmount = [
        orderStatesToShowOutstandingAmount.some(x => x.stage === cart.orderStage && x.paymentStatus === cart.orderPaymentStatus),
        cart.hasParent,
        cart.effectiveOutstandingAmountAfterCreditDiscount > 0 && cart.orderStage !== orderStages.canceled,
    ].some(element => element);

    // is not crowdfunding project or pledged is in edit mode
    const isPledgeInEditModeAndNotInCrowdfunding
        = !cartDetails.hasSameItemsAsParent && !pledgedOrderStates.some(x => x.stage === cart.orderStage && x.paymentStatus === cart.orderPaymentStatus)
        || projectContext.project.phase !== projectPhases.crowdfunding;

    // final condition for display outstanding amount.
    const effectiveShowOutstandingAmount = [
        hasOrderOutstandingAmount,
        isPledgeInEditModeAndNotInCrowdfunding,
    ].every(element => element);

    // show total paid amount conditions
    const showTotalPaidAmount = [
        cart.orderStage !== orderStages.unfinished,
        !cart.hasParent,
        cart.effectivePaidAmount > 0,
    ].every(element => element);

    // show total pledged value condition
    const effectiveShowTotalPledged = projectContext.project.version === projectVersions.v2
        && cartDetails.hasSameItemsAsParent
        && pledgedOrderStates.some(x => x.stage === cart.orderStage && x.paymentStatus === cart.orderPaymentStatus)
        || projectContext.project.phase === projectPhases.crowdfunding
        && pledgedOrderStates.some(x => x.stage === cart.orderStage && x.paymentStatus === cart.orderPaymentStatus)
        && cart.orderPaymentStatus === orderPaymentStatuses.none;
    const hasInstallmentsInProgress = [
        cart.orderStage === orderStages.pledged,
        cart.orderPaymentStatus === orderPaymentStatuses.scheduled,
        showTotalPaidAmount,
        cart.payableAmountConverted === 0,
    ].every(t => t);

    const shouldShowTotalPaidAndScheduledAmountBelowLine = () => {
        if (isOvercharged) return false;

        return cart.effectivePayableAmount === 0;
    };

    const totalDiscountMessages = cartDetails.customOrderItems
        .filter(( { orderItemType } ) => orderItemType === orderItemTypes.discount)
        .map( ( { displayName } ) => displayName)
        .join('; ');

    const shouldShowToPay = !isOvercharged && (hasInstallmentsInProgress ? false : effectiveShowOutstandingAmount) && cart.effectivePayableAmount > 0;
    const shouldShowToPayInEditMode = isEditModeEnabled && !isOvercharged && (hasInstallmentsInProgress ? false : effectiveShowOutstandingAmount);

    const totalPaid = cart.effectivePaidAmount + cart.effectiveRefundedAmount;
    const shouldShowTotalPaidAboveLine = (isOvercharged || cart.effectivePayableAmount > 0) && totalPaid > 0;
    const shouldShowTotalPaidAboveLineInEditMode = isOvercharged || cart.effectivePayableAmount === 0 && totalPaid > 0 && isEditModeEnabled;

    const taxDetailsRows = cart.taxInfos
        ? cart.taxInfos
            .filter(taxInfo => taxInfo.taxValue !== 0)
            .map(taxInfo => {
                return {
                    text: `${ convertToPercentage(taxInfo.taxRate, 2) }% - ${ Formatter.format(taxInfo.taxValue, 'C2', currencySymbol) }`,
                    dataQa: `tax-tooltip-row:${ taxInfo.taxRateType }`,
                };
            })
        : [];

    const taxDetails = taxDetailsRows.length > 0
        ? [{ text: 'Tax details:', dataQa: 'tax-tooltip-row:TaxDetails' }].concat(taxDetailsRows)
        : null;

    const totalTax = projectContext.project.version === projectVersions.v2
        ? cart.totalTax
        : cart.totalTaxConverted;

    const orderSummaryValuesModel = [
        {
            condition: true,
            title: isHandleTax
                ? Resources.YourPledge.OrderSummaryNetSubtotalLabel
                : Resources.YourPledge.OrderSummarySubtotalLabel,
            value: Formatter.format(isHandleTax ? cart.totalNetValueConverted : cart.totalValueConverted, 'C2', currencySymbol),
        },
        {
            condition: cart.effectiveTotalDiscount,
            title: isHandleTax
                ? Resources.YourPledge.OrderSummaryNetTotalDiscountsLabel
                : Resources.YourPledge.OrderSummaryTotalDiscountsLabel,
            tooltipText: totalDiscountMessages,
            value: Formatter.format(isHandleTax ? cart.effectiveTotalNetDiscount : cart.effectiveTotalDiscount, 'C2', currencySymbol),
        },
        {
            condition: true,
            title: isHandleTax
                ? Resources.YourPledge.OrderSummaryNetShippingCostLabel
                : Resources.YourPledge.OrderSummaryShippingCostLabel,
            value: isShippingDisabled
                ? '-'
                : Formatter.format(isHandleTax ? cart.shippingNetCostConverted : cart.shippingCostConverted, 'C2', currencySymbol),
            tooltipText: Resources.YourPledge.OrderSummaryShippingCostCollectedAfterCrowdfundingHint,
        },
        {
            condition: isHandleTax && !isVatReverseEnabled,
            title: cart.taxName
                ? Resources.format(Resources.YourPledge.OrderSummaryTaxWithNameLabel, cart.taxName)
                : Resources.YourPledge.OrderSummaryTaxLabel,
            hasSubtitle: !!locationTaxName,
            subtitle: locationTaxName,
            hasSubtitleTooltip: !!taxDetails,
            subtitleTooltipContent: taxDetails,
            value: cart.taxRate || cart.taxRate === 0 ? Formatter.format(Math.max(0, totalTax), 'C2', currencySymbol) : '-',
        },
        {
            condition: cart.totalVatDeductionConverted < 0,
            title: Resources.YourPledge.OrderSummaryTotalVatDeductionLabel,
            value: Formatter.format(cart.totalVatDeductionConverted, 'C2', currencySymbol),
        },
        {
            condition: shouldShowTotalPaidAboveLine || shouldShowTotalPaidAboveLineInEditMode,
            title: Resources.YourPledge.OrderSummaryTotalPaidLabel,
            value: Formatter.format(-totalPaid, 'C2', currencySymbol),
        },
        {
            condition: isOvercharged && cart.scheduledAmount,
            title: Resources.YourPledge.OrderSummaryRemainingInstallmentsLabel,
            value: Formatter.format(-cart.scheduledAmount, 'C2', currencySymbol),
        },
        {
            condition: showCreditDiscount,
            title: Resources.YourPledge.OrderSummaryCreditDiscountLabel,
            value: Formatter.format(cart.effectiveOutstandingCreditPaymentAmount * -1, 'C2', currencySymbol),
        },
        // divider to Separate single data from grouped data.
        {
            condition: effectiveShowTotalPledged || showTotalPaidAmount || effectiveShowOutstandingAmount,
            title: 'divider',
            isDivider: true,
        },
        {
            condition: effectiveShowTotalPledged,
            title: Resources.YourPledge.OrderSummaryTotalPledgedLabel,
            isHighlighted: true,
            theme: 'accented',
            value: Formatter.format(cart.effectiveOutstandingAmountAfterCreditDiscount, 'C2', currencySymbol),
        },
        {
            condition: shouldShowTotalPaidAndScheduledAmountBelowLine() && !isEditModeEnabled,
            title: Resources.YourPledge.OrderSummaryTotalPaidLabel,
            theme: 'accented',
            isHighlighted: true,
            value: Formatter.format(totalPaid, 'C2', currencySymbol),
        },
        {
            // #13894 - Have to turn off the "To Pay" property if there is nothing to pay in ongoing installments in the CF phase
            condition: shouldShowToPay || shouldShowToPayInEditMode,
            title: Resources.YourPledge.OrderSummaryToPayLabel,
            isHighlighted: true,
            value: Formatter.format(cart.effectivePayableAmount, 'C2', currencySymbol),
        },
        {
            condition: cart.effectivePayableAmount >= 0 && cart.scheduledAmount,
            title: Resources.YourPledge.OrderSummaryRemainingInstallmentsLabel,
            isHighlighted: true,
            value: Formatter.format(cart.scheduledAmount, 'C2', currencySymbol),
        },
        {
            condition: isOvercharged,
            title: Resources.YourPledge.OrderSummaryOverchargedLabel,
            isHighlighted: true,
            theme: 'invalid',
            value: Formatter.format(cart.effectivePayableAmount * -1, 'C2', currencySymbol),
        },
    ];

    return orderSummaryValuesModel.filter(orderSummaryItem => {
        if (orderSummaryItem.condition) {
            delete orderSummaryItem.condition;
            return orderSummaryItem;
        }
        return;
    });
}